import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { Lock, Eye, EyeOff, KeyRound } from 'lucide-react';
import { authApi, CHANGE_PASSWORD_MUTATION } from '../../api/auth-api';

const PasswordInput = ({ label, value, onChange, show, onToggle }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

const Profile = () => {
  const userData = authApi.getUserData();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [changePassword, { loading }] = useMutation(CHANGE_PASSWORD_MUTATION);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas.');
      return;
    }

    try {
      const { data } = await changePassword({
        variables: {
          userId: userData.id,
          currentPassword,
          newPassword,
        },
      });

      if (data?.changePassword) {
        toast.success('Mot de passe modifié avec succès.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error('Impossible de modifier le mot de passe.');
      }
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('incorrect') || msg.includes('invalid')) {
        toast.error('Mot de passe actuel incorrect.');
      } else {
        toast.error('Une erreur est survenue.');
      }
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Mon profil</h1>
        <p className="text-sm text-gray-500 mt-1">Gérez les informations de votre compte</p>
      </div>

      {/* User info card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="flex items-center space-x-4">
          <div className="h-14 w-14 rounded-full bg-green-600 text-white flex items-center justify-center text-xl font-bold">
            {userData?.firstName?.[0] ?? 'U'}
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900">
              {userData?.firstName} {userData?.lastName}
            </p>
            <p className="text-sm text-gray-500">{userData?.email}</p>
            <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              {userData?.userType === 'ADMIN' ? 'Administrateur' : 'Laboratoire'}
            </span>
          </div>
        </div>
      </div>

      {/* Change password card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center space-x-2 mb-5">
          <KeyRound size={18} className="text-green-600" />
          <h2 className="text-base font-semibold text-gray-900">Changer le mot de passe</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordInput
            label="Mot de passe actuel"
            value={currentPassword}
            onChange={setCurrentPassword}
            show={showCurrent}
            onToggle={() => setShowCurrent(!showCurrent)}
          />
          <PasswordInput
            label="Nouveau mot de passe"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            onToggle={() => setShowNew(!showNew)}
          />
          <PasswordInput
            label="Confirmer le nouveau mot de passe"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showConfirm}
            onToggle={() => setShowConfirm(!showConfirm)}
          />

          <button
            type="submit"
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            className="w-full mt-2 flex items-center justify-center space-x-2 bg-green-700 hover:bg-green-800 disabled:bg-gray-300 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
          >
            <Lock size={15} />
            <span>{loading ? 'Enregistrement…' : 'Enregistrer le nouveau mot de passe'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default Profile;
